/**
 * Intern Hub data. Tables and functions are in docs/intern-hub.sql; writes
 * that touch other people (enroll, move, exit) go through the server
 * (api/admin-actions?action=intern-admin). Everything here tolerates that
 * SQL not having been run yet: no hub, no pill, no errors on screen.
 */
import { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";

export async function internAdmin(op, payload = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Not authenticated");
  const res = await fetch("/api/admin-actions?action=intern-admin", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ op, origin: window.location.origin, ...payload }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `intern-admin ${res.status}`);
  return json;
}

/* Who this person is to the program. Read from their own rows, so it costs
   nothing for the coaches who aren't in it. Cached for the session. */
let accessPromise = null;
let accessUser = null;
export function loadInternAccess(userId) {
  if (!userId) return Promise.resolve({ intern: null, director: false, admin: false, leadOrgIds: [] });
  if (accessPromise && accessUser === userId) return accessPromise;
  accessUser = userId;
  accessPromise = (async () => {
    const [enr, me, leads] = await Promise.all([
      supabase.from("intern_enrollments").select("id, org_id, status, lead_id, started_on, program").eq("user_id", userId).maybeSingle(),
      supabase.from("profiles").select("is_admin, is_intern_director").eq("id", userId).maybeSingle(),
      supabase.from("org_members").select("org_id, role, organizations(kind)").eq("user_id", userId).in("role", ["owner", "admin"]),
    ]);
    const admin = !!me?.data?.is_admin;
    return {
      intern: !enr.error && enr.data && enr.data.status !== "exited" ? enr.data : null,
      director: admin || (!me.error && !!me.data?.is_intern_director),
      // Marked as a program director in their own right, not just by being
      // an admin. Only this one puts the hub in an admin's sidebar.
      namedDirector: !me.error && !!me.data?.is_intern_director,
      admin,
      leadOrgIds: (leads.data || []).filter(r => r.organizations?.kind === "location").map(r => r.org_id),
    };
  })().catch(() => ({ intern: null, director: false, admin: false, leadOrgIds: [] }));
  return accessPromise;
}
export function resetInternAccess() { accessPromise = null; accessUser = null; }

export function useInternAccess(userId) {
  const [access, setAccess] = useState(null);
  useEffect(() => {
    let off = false;
    loadInternAccess(userId).then(a => { if (!off) setAccess(a); });
    return () => { off = true; };
  }, [userId]);
  return access;
}

/** Can this person open the Intern Hub at all? */
/** Is the hub in this person's sidebar?
 *
 * Interns, named program directors, and location leads who approve hours.
 * NOT every admin (Coleman, 2026-10-16: "intern hub shouldn't be visible
 * unless you are signed in as an intern"): an admin counts as a director
 * inside the hub, so it still opens for them from #/interns, but it no
 * longer sits in the sidebar of everyone who runs the platform. */
export const hubVisible = (a) => !!a && (
  !!a.intern || !!a.namedDirector || (!a.admin && a.leadOrgIds.length > 0)
);

export async function loadRoster(orgId = null) {
  const { data, error } = await supabase.rpc("intern_roster", { p_org_id: orgId });
  if (error) throw error;
  return data || [];
}

export async function listHours(enrollmentId) {
  const { data, error } = await supabase.from("intern_hours").select("*")
    .eq("enrollment_id", enrollmentId).order("session_date", { ascending: false }).order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function listPendingHours(enrollmentIds) {
  if (!enrollmentIds.length) return [];
  const { data, error } = await supabase.from("intern_hours").select("*")
    .in("enrollment_id", enrollmentIds).eq("status", "pending").order("session_date", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function logHours(entry) {
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase.from("intern_hours").insert({ ...entry, user_id: user.id }).select().single();
  if (error) throw error;
  return data;
}

/** Change an entry that's still pending (the database refuses anything else). */
export async function updateHours(id, patch) {
  const { data, error } = await supabase.from("intern_hours").update(patch).eq("id", id).eq("status", "pending").select().maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("That entry has already been reviewed.");
  return data;
}

export async function deleteHours(id) {
  const { error } = await supabase.from("intern_hours").delete().eq("id", id);
  if (error) throw error;
}

export async function verifyHours(ids, status, note = null) {
  const { data, error } = await supabase.rpc("verify_intern_hours", { p_ids: ids, p_status: status, p_note: note });
  if (error) throw error;
  return data;
}

export async function setWeek(enrollmentId, weekStart, kind, status, note = null) {
  const { error } = await supabase.rpc("set_intern_week", { p_enrollment: enrollmentId, p_week: weekStart, p_kind: kind, p_status: status, p_note: note });
  if (error) throw error;
}

export async function internCard(userId) {
  const { data, error } = await supabase.rpc("intern_card", { p_user_id: userId });
  if (error) return null;
  return data?.[0] || null;
}

/* The INTERN pill: who's an active intern, and their location's state.
   One small read per session, shared by every name on screen. */
let badgeMap = null;
let badgePromise = null;
const badgeListeners = new Set();
let badgeFailedAt = 0;
function loadBadges() {
  if (!badgePromise && Date.now() - badgeFailedAt < 60000) return Promise.resolve(badgeMap || {});
  if (!badgePromise) {
    badgePromise = supabase.rpc("intern_badges").then(({ data, error }) => {
      if (error) { badgePromise = null; badgeFailedAt = Date.now(); return badgeMap || {}; } // retry in a minute rather than hide every pill
      badgeMap = Object.fromEntries((data || []).map(r => [r.user_id, r.state_code || ""]));
      badgeListeners.forEach(fn => fn(badgeMap));
      return badgeMap;
    }, () => { badgePromise = null; badgeFailedAt = Date.now(); return badgeMap || {}; });
  }
  return badgePromise;
}
export function refreshInternBadges() { badgePromise = null; return loadBadges(); }

/** null when not an intern; otherwise the state code ("" if none set). */
export function useInternBadge(userId) {
  const [map, setMap] = useState(badgeMap);
  useEffect(() => {
    if (!userId) return undefined;
    badgeListeners.add(setMap);
    if (!badgeMap) loadBadges();
    return () => { badgeListeners.delete(setMap); };
  }, [userId]);
  if (!userId || !map) return null;
  return Object.prototype.hasOwnProperty.call(map, userId) ? map[userId] : null;
}

/** Can this person open the hub when they get there (admins included)? */
export const hubOpens = (a) => !!a && (!!a.intern || a.director || a.leadOrgIds.length > 0);
